import { useCallback, useEffect, useState } from 'react';
import { PageHeader, EmptyState, TableSkeleton } from '../components/common/PageHeader';
import { SearchFilterBar } from '../components/common/SearchFilterBar';
import { Modal, DeleteConfirmModal } from '../components/common/Modal';
import { FeaturedBadge } from '../components/common/StatusBadge';
import ComboForm, {
  emptyComboForm,
  formToComboFormData,
  comboToForm,
} from '../components/forms/ComboForm';
import { listCombos, createCombo, updateCombo, deleteCombo } from '../services/comboService';
import { listProducts } from '../services/productService';
import { getErrorMessage } from '../services/apiClient';
import { useToast } from '../context/ToastContext';
import { formatCurrency } from '../utils/format';

export default function CombosPage() {
  const { showToast } = useToast();
  const [combos, setCombos] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  const [form, setForm] = useState(emptyComboForm);

  const fetchCombos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listCombos({ limit: 100 });
      setCombos(res.data || []);
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchCombos();
    listProducts({ limit: 100 })
      .then((res) => setProducts(res.data || []))
      .catch(() => {});
  }, [fetchCombos]);

  const filtered = combos.filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return c.title.toLowerCase().includes(q) || c.slug?.toLowerCase().includes(q);
  });

  const openCreate = () => {
    setEditing(null);
    setForm(emptyComboForm);
    setModalOpen(true);
  };

  const openEdit = (combo) => {
    setEditing(combo);
    setForm(comboToForm(combo));
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title || form.price === '') {
      showToast('Title and price are required', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = formToComboFormData(form);
      if (editing) {
        await updateCombo(editing._id, payload);
        showToast('Combo updated successfully');
      } else {
        await createCombo(payload);
        showToast('Combo created successfully');
      }
      setModalOpen(false);
      fetchCombos();
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingItem) return;
    setDeleting(true);
    try {
      await deleteCombo(deletingItem._id);
      showToast('Combo deleted successfully');
      setDeleteOpen(false);
      setDeletingItem(null);
      fetchCombos();
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Combo Offers"
        description="Manage bundled product offers"
        action={<button type="button" className="btn btn-primary" onClick={openCreate}>+ Add Combo</button>}
      />

      <div className="card">
        <SearchFilterBar search={search} onSearchChange={setSearch} searchPlaceholder="Search combos..." onClear={() => setSearch('')} />

        {loading ? (
          <TableSkeleton cols={5} />
        ) : filtered.length === 0 ? (
          <EmptyState title="No combos found" action={{ label: 'Add Combo', onClick: openCreate }} />
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Combo</th>
                  <th>Price</th>
                  <th>Products</th>
                  <th>Consultation</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((combo) => (
                  <tr key={combo._id}>
                    <td>
                      <div className="cell-product">
                        {combo.image && <img src={combo.image} alt="" className="cell-thumb" />}
                        <div>
                          <p className="cell-title">{combo.title}</p>
                          <p className="cell-sub">{combo.tagline}</p>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span>{formatCurrency(combo.price)}</span>
                      {combo.originalPrice && (
                        <span className="cell-sub line-through">{formatCurrency(combo.originalPrice)}</span>
                      )}
                    </td>
                    <td>{combo.products?.length || 0} items</td>
                    <td>{combo.includesConsultation ? '✓ Yes' : '—'}</td>
                    <td><FeaturedBadge featured={combo.featured} /></td>
                    <td>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => openEdit(combo)}>Edit</button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm text-danger"
                        onClick={() => {
                          setDeletingItem(combo);
                          setDeleteOpen(true);
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Edit Combo' : 'Create Combo'}
        size="lg"
      >
        <form onSubmit={handleSave}>
          <ComboForm form={form} onChange={setForm} products={products} />
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={() => setModalOpen(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>

      <DeleteConfirmModal
        open={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setDeletingItem(null);
        }}
        onConfirm={handleDelete}
        title="Delete Combo Offer"
        itemName={deletingItem?.title}
        loading={deleting}
      />
    </div>
  );
}
